import { useCallback, useState } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import AppendableBlocks from "@/components/feature/AppendableBlocks";
import { defaultMission, type MissionData, type Pillar } from "@/data/siteContent";

const ICON_OPTIONS = [
  "ri-store-2-line", "ri-money-dollar-circle-line", "ri-shield-check-line", "ri-community-line",
  "ri-heart-line", "ri-star-line", "ri-palette-line", "ri-leaf-line",
  "ri-award-line", "ri-hand-heart-line", "ri-lightbulb-line", "ri-rocket-line",
  "ri-user-star-line", "ri-global-line", "ri-home-heart-line", "ri-gift-line",
  "ri-seedling-line", "ri-plant-line", "ri-recycle-line", "ri-earth-line",
];

function PillarIconPicker({
  currentIcon,
  onSelect,
  onClose,
}: {
  currentIcon: string;
  onSelect: (icon: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState(currentIcon);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 w-80 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-bold text-sm">Choose Icon</h3>
        <div className="flex flex-wrap gap-1.5">
          {ICON_OPTIONS.map((ic) => (
            <button
              key={ic}
              onClick={() => setSelected(ic)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${selected === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}
            >
              <i className={`${ic} text-base`}></i>
            </button>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={() => { onSelect(selected); onClose(); }} className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap">Apply</button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
        </div>
      </div>
    </div>
  );
}

interface OurMissionSectionProps { dataKey?: string; }

export default function OurMissionSection({ dataKey }: OurMissionSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [data, setData] = useAdminStorage<MissionData>(dataKey ?? "ma_mission", defaultMission);
  const [iconPickerIdx, setIconPickerIdx] = useState<number | null>(null);

  const set = useCallback(<K extends keyof MissionData>(key: K, val: MissionData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updatePillar = useCallback((i: number, field: keyof Pillar, val: string) => {
    const pillars = data.pillars.map((p, idx) => idx === i ? { ...p, [field]: val } : p);
    setData({ ...data, pillars });
  }, [data, setData]);

  const addPillar = useCallback(() => {
    const newPillar: Pillar = {
      icon: ICON_OPTIONS[data.pillars.length % ICON_OPTIONS.length],
      title: "New Pillar",
      body: "Click to edit this description.",
    };
    setData({ ...data, pillars: [...data.pillars, newPillar] });
  }, [data, setData]);

  const removePillar = useCallback((i: number) => {
    setData({ ...data, pillars: data.pillars.filter((_, idx) => idx !== i) });
  }, [data, setData]);

  const bg = isDark ? "bg-gray-900" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const pillarIconBg = isDark ? "bg-pink-900/40" : "bg-pink-50";

  return (
    <section id="mission" className={`py-24 px-6 transition-colors duration-300 ${bg}`}>
      {iconPickerIdx !== null && (
        <PillarIconPicker
          currentIcon={data.pillars[iconPickerIdx]?.icon || "ri-star-line"}
          onSelect={(icon) => updatePillar(iconPickerIdx, "icon", icon)}
          onClose={() => setIconPickerIdx(null)}
        />
      )}
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          {(isAdmin || data.eyebrow) && (
            isAdmin ? (
              <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} onDelete={() => set("eyebrow", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
            )
          )}
          {(isAdmin || data.heading) && (
            isAdmin ? (
              <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-3 mb-0" inputClassName="text-4xl font-extrabold text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
            )
          )}
        </div>

        {/* Founder Story — Photo + Story stacked, then Mission card below photo */}
        <div className="flex flex-col lg:flex-row gap-12 items-start mb-20">
          {/* Left column: Photo + Mission card */}
          <div className="w-full lg:w-2/5 flex-shrink-0 space-y-6">
            {/* Photo */}
            {(isAdmin || data.founderImage) && (
              <div className="w-full h-[420px] rounded-3xl overflow-hidden relative group/photo">
                {isAdmin && data.founderImage && (
                  <button
                    onClick={() => set("founderImage", "")}
                    className="absolute top-3 right-3 z-30 w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer transition-colors shadow-sm"
                    title="Remove photo"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
                {isAdmin ? (
                  <AdminImageUpload
                    src={data.founderImage}
                    alt={data.founderName}
                    onReplace={(url) => set("founderImage", url)}
                    onDelete={() => set("founderImage", "")}
                    objectPosition={(data as MissionData & { founderImagePosition?: string }).founderImagePosition ?? "50% 20%"}
                    onPositionChange={(pos) => set("founderImagePosition" as keyof MissionData, pos as MissionData[keyof MissionData])}
                    mobileObjectPosition={(data as MissionData & { founderImageMobilePosition?: string }).founderImageMobilePosition ?? "50% 20%"}
                    onMobilePositionChange={(pos) => set("founderImageMobilePosition" as keyof MissionData, pos as MissionData[keyof MissionData])}
                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover object-top"
                  />
                ) : (
                  <img src={data.founderImage} alt={data.founderName} className="w-full h-full object-cover object-top" />
                )}
                <div className="absolute bottom-0 left-0 right-0 p-6" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)" }}>
                  {isAdmin ? (
                    <>
                      <AdminEditField value={data.founderName} onSave={(v) => set("founderName", v)} inputClassName="text-white font-bold text-lg">
                        <div className="text-white font-bold text-lg">{data.founderName}</div>
                      </AdminEditField>
                      <AdminEditField value={data.founderTitle} onSave={(v) => set("founderTitle", v)} inputClassName="text-white/70 text-sm">
                        <div className="text-white/70 text-sm">{data.founderTitle}</div>
                      </AdminEditField>
                    </>
                  ) : (
                    <>
                      <div className="text-white font-bold text-lg">{data.founderName}</div>
                      <div className="text-white/70 text-sm">{data.founderTitle}</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Mission Statement Card — now BELOW the photo */}
            {(isAdmin || data.missionStatement) && (
              <div className={`rounded-2xl p-6 relative group/mission ${isDark ? "bg-gray-800" : ""}`} style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}>
                {isAdmin && (
                  <button
                    onClick={() => set("missionStatement", "")}
                    className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors shadow-sm"
                    title="Remove mission card"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-pink-400 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-heart-line text-white text-sm"></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-bold uppercase tracking-widest text-pink-500 mb-2">Our Mission</div>
                    {isAdmin ? (
                      <AdminEditField value={data.missionStatement} onSave={(v) => set("missionStatement", v)} multiline richText inputClassName="font-medium text-base italic leading-relaxed">
                        <RichText html={`"${data.missionStatement}"`} as="p" className={`font-medium text-base italic leading-relaxed ${isDark ? "text-white" : "text-gray-800"}`} />
                      </AdminEditField>
                    ) : (
                      <RichText html={`"${data.missionStatement}"`} as="p" className={`font-medium text-base italic leading-relaxed ${isDark ? "text-white" : "text-gray-800"}`} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column: Founder story only */}
          <div className="w-full lg:w-3/5">
            <div className="w-12 h-1 rounded-full bg-pink-400 mb-6"></div>
            {isAdmin ? (
              <AdminEditField value={data.founderStory} onSave={(v) => set("founderStory", v)} multiline richText className="mb-8" inputClassName="leading-relaxed text-base">
                <RichText html={data.founderStory} as="div" className={`leading-relaxed text-base ${subColor}`} />
              </AdminEditField>
            ) : (
              <RichText html={data.founderStory} as="div" className={`leading-relaxed text-base mb-8 ${subColor}`} />
            )}
          </div>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.pillars.map((pillar, i) => (
            <div key={i} className={`rounded-2xl p-6 border hover:-translate-y-1 transition-transform relative group/pillar ${cardBg}`}>
              {isAdmin && (
                <button
                  onClick={() => removePillar(i)}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 opacity-0 group-hover/pillar:opacity-100 transition-opacity"
                  title="Remove pillar"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 relative group/icon ${pillarIconBg}`}>
                <i className={`${pillar.icon} text-pink-500 text-2xl`}></i>
                {isAdmin && (
                  <button
                    onClick={() => setIconPickerIdx(i)}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl opacity-0 group-hover/icon:opacity-100 transition-opacity cursor-pointer"
                    title="Change icon"
                  >
                    <i className="ri-edit-line text-white text-xs"></i>
                  </button>
                )}
              </div>
              {isAdmin ? (
                <>
                  <AdminEditField value={pillar.title} onSave={(v) => updatePillar(i, "title", v)} className="mb-2" inputClassName="font-bold text-base">
                    <h4 className={`font-bold text-base mb-2 ${headingColor}`}>{pillar.title}</h4>
                  </AdminEditField>
                  <AdminEditField value={pillar.body} onSave={(v) => updatePillar(i, "body", v)} multiline richText inputClassName="text-sm leading-relaxed">
                    <RichText html={pillar.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                  </AdminEditField>
                </>
              ) : (
                <>
                  <h4 className={`font-bold text-base mb-2 ${headingColor}`}>{pillar.title}</h4>
                  <RichText html={pillar.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                </>
              )}
            </div>
          ))}

          {/* Add pillar card (admin only) */}
          {isAdmin && (
            <button
              onClick={addPillar}
              className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 p-6 cursor-pointer transition-all hover:border-pink-400 hover:bg-pink-50/50 ${
                isDark ? "border-gray-600 text-gray-500 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:text-pink-500"
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100">
                <i className="ri-add-line text-pink-500 text-xl"></i>
              </div>
              <span className="text-sm font-semibold">Add Container</span>
            </button>
          )}
        </div>

        <AppendableBlocks blocks={data.extraBlocks ?? []} onChange={(v) => set("extraBlocks", v)} className="mt-10" />
      </div>
    </section>
  );
}